import { useEffect, useRef, useState } from 'react';

import { szamotOlvas } from '../formazas.ts';

/**
 * Helyben szerkeszthető mező.
 *
 * A mentés akkor indul, amikor elhagyod a mezőt (vagy Entert ütsz) — és
 * csak akkor, ha tényleg megváltozott az érték. **Minden mező külön kérés**,
 * ahogy a megbízás előírja: így a többfelhasználós működésnél nem írja
 * felül két ember egymás munkáját.
 *
 * Ha a mentés nem sikerül, a mező pirosra vált, az eredeti érték visszaáll,
 * és az ok a mező alatt olvasható. Nem tűnik el csendben semmi.
 */

export interface MezoProps {
  ertek: string | number | null;
  /** A mentést végző hívás. Ha hibát dob, a mező visszaáll. */
  onMent: (uj: string) => Promise<void>;
  fajta?: 'szoveg' | 'szam';
  helykitolto?: string;
  cim?: string;
  /** Ellenőrzés mentés előtt. Ha szöveget ad vissza, az a hibaüzenet. */
  ellenoriz?: (ertek: string) => string | null;
  tiltva?: boolean;
  /**
   * A mező osztályneve. Táblázatonként más kell: a törzsadat-táblában `txt`,
   * a járatlap-jellegű táblákban `c`. Ha nem adod meg, a fajta dönt.
   */
  osztaly?: string;
}

export function Mezo({
  ertek,
  onMent,
  fajta = 'szoveg',
  helykitolto = '—',
  cim,
  ellenoriz,
  tiltva,
  osztaly,
}: MezoProps) {
  const eredeti = ertek === null || ertek === undefined ? '' : String(ertek);
  const [szoveg, setSzoveg] = useState(eredeti);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ment, setMent] = useState(false);
  const utolsoMentett = useRef(eredeti);

  // Ha kívülről változik az érték (pl. újratöltés után), kövessük.
  useEffect(() => {
    if (eredeti !== utolsoMentett.current) {
      utolsoMentett.current = eredeti;
      setSzoveg(eredeti);
    }
  }, [eredeti]);

  async function mentes() {
    const uj = szoveg.trim();
    if (uj === utolsoMentett.current.trim()) return;

    const baj = ellenoriz?.(uj) ?? null;
    if (baj) {
      setHiba(baj);
      return;
    }

    setMent(true);
    setHiba(null);
    try {
      await onMent(uj);
      utolsoMentett.current = uj;
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
      setSzoveg(utolsoMentett.current);
    } finally {
      setMent(false);
    }
  }

  return (
    <>
      <input
        className={osztaly ?? (fajta === 'szoveg' ? 'txt' : undefined)}
        value={szoveg}
        placeholder={helykitolto}
        title={hiba ?? cim}
        disabled={tiltva || ment}
        style={hiba ? { borderColor: 'var(--bad)', background: 'var(--badw)' } : undefined}
        onChange={(e) => {
          setSzoveg(e.target.value);
          if (hiba) setHiba(null);
        }}
        /*
         * A számmező fókuszáláskor kijelöli magát: aki javítani kattint bele,
         * ne a régi szám mellé írjon. A szövegnél ez rossz volna — ott a
         * meglévő megjegyzést szoktuk kiegészíteni. (2026-09-30)
         */
        onFocus={(e) => {
          if (fajta === 'szam') e.currentTarget.select();
        }}
        onBlur={() => void mentes()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setSzoveg(utolsoMentett.current);
            setHiba(null);
            e.currentTarget.blur();
          }
        }}
      />
      {hiba && <div className="mezohiba">{hiba}</div>}
    </>
  );
}

/** Szám bevitele — a vesszőt és a szóközöket is elfogadja. */
export function SzamMezo(props: Omit<MezoProps, 'fajta'>) {
  return (
    <Mezo
      {...props}
      fajta="szam"
      ellenoriz={(e) => {
        if (e && szamotOlvas(e) === null) return 'Ez nem szám.';
        return props.ellenoriz?.(e) ?? null;
      }}
    />
  );
}

/**
 * Legördülő választék, ami azonnal ment.
 *
 * Az üres választ csak ott kínáljuk fel, ahol van értelmes „nincs megadva"
 * állapot (például a fizetési módnál az üres = készpénz). Ahol mindig kell
 * választani — mint a gyakoriságnál —, ott a `kotelezo` jelölővel elhagyjuk.
 */
export function ValasztoMezo({
  ertek,
  lehetosegek,
  onMent,
  uresCimke,
  kotelezo = false,
  osztaly,
  tiltva,
}: {
  ertek: string | null;
  lehetosegek: Array<{ ertek: string; cimke: string }>;
  onMent: (uj: string) => Promise<void>;
  uresCimke?: string;
  kotelezo?: boolean;
  osztaly?: string;
  tiltva?: boolean;
}) {
  const [ment, setMent] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);

  return (
    <>
      <select
        className={osztaly}
        value={ertek ?? ''}
        disabled={ment || tiltva}
        title={hiba ?? undefined}
        style={hiba ? { borderColor: 'var(--bad)' } : undefined}
        onChange={async (e) => {
          setMent(true);
          setHiba(null);
          try {
            await onMent(e.target.value);
          } catch (h) {
            setHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
          } finally {
            setMent(false);
          }
        }}
      >
        {!kotelezo && <option value="">{uresCimke ?? '—'}</option>}
        {lehetosegek.map((l) => (
          <option key={l.ertek} value={l.ertek}>
            {l.cimke}
          </option>
        ))}
      </select>
      {hiba && <div className="mezohiba">{hiba}</div>}
    </>
  );
}
